import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const NAV_SERVICES = [
  { slug: "social-media-management", title: "Social Media Management" },
  { slug: "content-strategy", title: "Content Strategy" },
  { slug: "website-design", title: "Website Design" },
  { slug: "ai-consultancy", title: "AI Consultancy" },
];

const NAV = [
  { label: "About", to: "/about" },
  { label: "Services", to: "/services", hasDropdown: true },
  { label: "Work", to: "/work" },
  { label: "Insights", to: "/insights" },
  { label: "Contact", to: "/contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-md shadow-[0_10px_30px_-18px_rgba(22,22,29,0.35)]" : "bg-transparent"
      }`}
    >
      <nav className="td-container flex items-center justify-between h-[74px]">
        <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-1.5 group">
          <span className="font-head text-xl font-semibold tracking-tight text-black">Track</span>
          <span className="font-head text-xl font-semibold tracking-tight text-[#1a1a1a]">Digital</span>
          <span className="w-1.5 h-1.5 rounded-full bg-[#7C6CF0] mb-1 transition-transform group-hover:scale-150" />
        </Link>

        <div className="hidden lg:flex items-center gap-9">
          {NAV.map((item) =>
            item.hasDropdown ? (
              <div
                key={item.to}
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <Link
                  to={item.to}
                  data-testid="nav-services"
                  className={`relative flex items-center gap-1 text-sm font-medium transition-colors ${isActive("/services") ? "text-black" : "text-[#2b2b2b] hover:text-black"}`}
                >
                  {item.label}
                  <ChevronDown size={14} className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
                  {isActive("/services") && <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-[#7C6CF0] rounded-full" />}
                </Link>
                {servicesOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4">
                    <div className="w-[300px] bg-white rounded-2xl border border-[#d3d3d3]/50 shadow-xl p-2">
                      {NAV_SERVICES.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/services/${s.slug}`}
                          data-testid={`nav-dropdown-${s.slug}`}
                          className="block px-4 py-2.5 rounded-xl text-sm text-[#2b2b2b] hover:bg-[#fafafa] hover:text-black transition-colors"
                        >
                          {s.title}
                        </Link>
                      ))}
                      <Link
                        to="/services"
                        data-testid="nav-dropdown-all"
                        className="block px-4 py-2.5 rounded-xl text-sm font-medium text-[#7C6CF0] hover:bg-[#fafafa] transition-colors"
                      >
                        View all services
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`relative text-sm font-medium transition-colors ${isActive(item.to) ? "text-black" : "text-[#2b2b2b] hover:text-black"}`}
              >
                {item.label}
                {isActive(item.to) && <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-[#7C6CF0] rounded-full" />}
              </Link>
            )
          )}
        </div>

        <div className="hidden lg:block">
          <Link to="/contact" data-testid="nav-cta" className="td-btn td-btn-primary !py-3 !px-6 text-sm">
            Book a Discovery Call
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-black"
          onClick={() => setMobileOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-[#d3d3d3]/40">
          <div className="td-container py-6 flex flex-col gap-1">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="py-3 text-base font-medium text-[#2b2b2b] border-b border-[#f0f0f0]"
              >
                {item.label}
              </Link>
            ))}
            <div className="pl-3 py-2 flex flex-col">
              {NAV_SERVICES.map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} className="py-2 text-sm text-[#6b6b6b]">
                  {s.title}
                </Link>
              ))}
              <Link to="/services" className="py-2 text-sm font-medium text-[#7C6CF0]">View all services</Link>
            </div>
            <Link to="/contact" className="td-btn td-btn-primary mt-4">
              Book a Discovery Call
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
